import { z } from 'zod';
import { DocId, IsoTimestamp } from '../common.js';
import { sourceDefaultClassificationFields } from '../classification.js';
/**
 * Phase 1.5 — Confluence Cloud ingest sources.
 *
 * One row per "subtree-rooted-at-page-X" admin selection. The actual
 * pages live as normal `kennisbank_ring{N}_documents/...` registry rows
 * with filenames `confluence-{pageId}-{slug}.md` so the existing
 * retrieval pipeline treats them like any other doc.
 *
 * Top-level collection: `confluence_sources/{id}`. Cross-tenant; kb_admin
 * RBAC. Shared-corpus exception is implicit — the resulting documents land
 * in the same Ring 0 / Ring 1 collections that already carry the documented
 * exception in CLAUDE.md.
 */
export const ConfluenceSyncStatus = z.enum(['idle', 'running', 'success', 'partial', 'failed']);
export const ConfluenceSyncStats = z.object({
    pagesAttempted: z.number().int().nonnegative(),
    pagesSucceeded: z.number().int().nonnegative(),
    pagesFailed: z.number().int().nonnegative(),
    /** Pages where stored confluenceVersion matched Confluence's current
     *  version — delta-skipped without re-embedding. Phase 2 added this. */
    pagesUnchanged: z.number().int().nonnegative().default(0),
    /** Pages skipped because the registry entry has blockedFromIngest=true.
     *  Distinct from pagesUnchanged: blocked pages were tombstoned by an
     *  admin who doesn't want them re-ingested even when they change. */
    pagesBlocked: z.number().int().nonnegative().default(0),
    /** Last error blob — first failure or summary. */
    error: z.string().optional(),
});
export const ConfluenceSyncPhase = z.enum([
    'listing',
    'metadata',
    'fetching',
    'ingesting',
    'completed',
]);
export const ConfluenceSyncPageStatus = z.enum([
    'in_progress',
    'succeeded',
    'unchanged',
    'failed',
    'blocked',
]);
/**
 * Rolling per-page log written alongside every syncProgress update.
 * Bounded to 20 entries (FIFO, newest pushed at the tail) so the UI can
 * stream a "what just happened" log without re-fetching anything else.
 */
export const ConfluenceSyncPageEntry = z.object({
    pageId: z.string().min(1).max(40),
    title: z.string().max(200),
    status: ConfluenceSyncPageStatus,
    /** ISO. Time of the row's last update. */
    ts: IsoTimestamp,
    /** Total wall-clock from this page's `in_progress` write to its terminal write. */
    durationMs: z.number().int().nonnegative().optional(),
    /** Present only on `failed` rows; max 300 chars. */
    errorMessage: z.string().max(300).optional(),
});
/**
 * Live progress while a sync is in flight. Cleared/replaced on each new
 * sync. Frontend polls and renders a progress strip when present +
 * lastSyncStatus === 'running'.
 */
export const ConfluenceSyncProgress = z.object({
    totalDescendants: z.number().int().nonnegative(),
    currentIndex: z.number().int().nonnegative(),
    currentTitle: z.string().max(500).optional(),
    phase: ConfluenceSyncPhase,
    pagesAttempted: z.number().int().nonnegative(),
    pagesSucceeded: z.number().int().nonnegative(),
    pagesFailed: z.number().int().nonnegative(),
    pagesUnchanged: z.number().int().nonnegative(),
    pagesBlocked: z.number().int().nonnegative().default(0),
    startedAt: IsoTimestamp,
    updatedAt: IsoTimestamp,
    /**
     * Last 20 page rows, oldest first. Optional + default([]) so older docs
     * (and the list endpoint) parse cleanly. The UI renders newest-first.
     */
    recentPages: z.array(ConfluenceSyncPageEntry).max(50).default([]).optional(),
});
export const ConfluenceSource = z.object({
    id: DocId,
    name: z.string().min(1).max(100),
    /** e.g. `onenebula.atlassian.net` — host only, NO protocol. */
    cloudBaseUrl: z.string().min(1).max(120),
    /** Confluence page IDs are numeric strings. */
    rootPageId: z.string().regex(/^\d+$/),
    ring: z.enum(['ring0', 'ring1']),
    lastSyncedAt: IsoTimestamp.optional(),
    lastSyncStatus: ConfluenceSyncStatus.default('idle'),
    lastSyncStats: ConfluenceSyncStats.optional(),
    syncProgress: ConfluenceSyncProgress.optional(),
    createdAt: IsoTimestamp,
    createdBy: DocId,
    /**
     * ISO 27001 classification defaults that cascade to every doc ingested
     * from this source. Operator can downgrade per-doc post-ingest via the
     * admin PATCH route. Older sources without these read as internal / [].
     */
    ...sourceDefaultClassificationFields,
    /**
     * Specialisations attached at the source level — applied to every page
     * ingested from this Confluence space so the Knowledge Agent's chip
     * filters and the Documents browse rail can scope by topic without
     * re-tagging every doc. Names must exist in the `specialisations/`
     * Firestore collection (validated at write time by the admin route).
     * Edited only via /admin/specialisations — not surfaced on the
     * Confluence sources page itself. Cap mirrors the 10-tag document cap.
     */
    specialisations: z.array(z.string().min(1).max(60)).max(10).default([]).optional(),
});
//# sourceMappingURL=confluence-source.js.map